import { FormField } from '@shared/components/add-form/add-form.component';
import { PurchaseOrderFieldItem } from '@app/feature/configuration/order/purchase-order-config/purchase-order-config.config';
import type { PurchaseOrderDetailItem } from './purchase-order-add/purchase-order-add.config';

/**
 * Apply purchase order field config (required/active) to form fields.
 * Used by View, Edit, Add to sync form required state with config.
 */
export function applyPurchaseOrderConfigToFields(
  config: PurchaseOrderFieldItem[],
  currentFields: FormField[]
): FormField[] {
  if (!currentFields?.length) return currentFields;

  const configMap = new Map(
    config.map((f) => [f.fieldKey, f] as [string, PurchaseOrderFieldItem])
  );

  return currentFields.map((field) => {
    const fieldConfig = configMap.get(field.controlName);
    if (fieldConfig) {
      return {
        ...field,
        required: fieldConfig.isActive && fieldConfig.isRequired,
      };
    }
    return field;
  });
}

/** Label/section prefix for view vs edit (purchase_order.add → purchase_order.view/edit) */
export function withModeLabels(
  field: FormField,
  mode: 'view' | 'edit'
): FormField {
  const prefix = `purchase_order.${mode}`;
  return {
    ...field,
    label: (field.label || '').replace('purchase_order.add', prefix),
    section: (field.section || '').replace('purchase_order.add', prefix),
  };
}

const PURCHASE_ORDER_STATUS_OPTIONS = [
  { label: 'purchase_order.status.pending', value: 'PENDING' },
  { label: 'purchase_order.status.approved', value: 'APPROVED' },
  { label: 'purchase_order.status.rejected', value: 'REJECTED' },
  { label: 'purchase_order.status.sent', value: 'SENT' },
  {
    label: 'purchase_order.status.vendor_confirmed',
    value: 'VENDOR_CONFIRMED',
  },
  { label: 'purchase_order.status.delivered', value: 'DELIVERED' },
  { label: 'purchase_order.status.done', value: 'DONE' },
  { label: 'purchase_order.status.closed', value: 'CLOSED' },
  { label: 'purchase_order.status.cancelled', value: 'CANCELLED' },
];

/**
 * Build status field for View/Edit. Shared options; label/section vary by mode.
 */
export function getPurchaseOrderStatusField(
  mode: 'view' | 'edit',
  status?: string
): FormField {
  const prefix = `purchase_order.${mode}`;
  return {
    controlName: 'status',
    label: `${prefix}.fields.status`,
    type: 'dropdown',
    section: `${prefix}.sections.basic_information`,
    required: true,
    disabled: true,
    readonly: true,
    style:
      'background-color: #f5f5f5; color: #666; cursor: not-allowed;',
    defaultValue: status,
    options: PURCHASE_ORDER_STATUS_OPTIONS,
  } as FormField;
}

/**
 * Map API purchase order items to PurchaseOrderDetailItem[] for View/Edit.
 * API uses tax as decimal (0.08); UI uses percentage (8).
 */
export function mapPurchaseOrderItemsToDetailList(
  items: { [key: string]: any }[]
): PurchaseOrderDetailItem[] {
  return items.map((item) => {
    const taxVal = item['tax'];
    const taxPct = taxVal != null ? taxVal * 100 : 0;
    const qty = item['quantity'] || 0;
    const price = item['price'] || 0;
    const tax = item['tax'] ?? 0;
    return {
      id: (item['id'] as unknown)?.toString() || '',
      skuCode: item['skuCode'] || '',
      skuName: item['skuName'] || '',
      vendorSkuCode: item['vendorSkuCode'] || '',
      vendorSkuName: item['vendorSkuName'] || '',
      spuCode: item['spuCode'] || '',
      spuName: item['spuName'] || '',
      quantity: qty,
      unit: 'cái',
      unitPrice: price,
      tax: taxPct,
      totalValue: qty * price * (1 + tax),
      purchaseRequestItem: item['purchaseRequestItem'] ?? null,
      warehouseCode: item['warehouseCode'] || '',
      warehouseName: item['warehouseName'] || '',
      receivedQuantity: item['receivedQuantity'] ?? 0,
    };
  });
}
